import * as qs from 'qs'
import type { UseFetchOptions } from 'nuxt/app'
import { DateRequestSimilarEventsResponse } from './types/event'

export const useSubscribeEmailToEventUpdates = () => {
  /** Wrapper for API requests */
  const error = ref(null)
  const loading = ref(false)
  const subscribeEmailToEventUpdates = async ({
    permalink,
    email,
  }: {
    permalink: string
    email: string
  }) => {
    // Logger.debug(`useSubscribeEmailToEventUpdates/${id}/subscribeEmailToEventUpdates`, id)

    try {
      loading.value = true
      await $fetch(`v1/store/events/${permalink}/subscribe`, {
        method: 'POST',
        body: { email },
      })

      error.value = null
    } catch (err) {
      error.value = (err as any)?.response?.data || err
      // Logger.error(`useSubscribeEmailToEventUpdates/${id}/subscribeEmailToEventUpdates`, err)
    } finally {
      loading.value = false
    }
  }

  return {
    subscribeEmailToEventUpdates,
    error,
  }
}

export const useFetchEventByPermalink = <T, R = T>(
  permalink: string,
  options?: UseFetchOptions<T, R>
) =>
  useKftApiFetch<T, R>(
    `v1/store/events/${permalink}?${qs.stringify(
      {
        include: ['address.locality', 'supplier', 'reviews'],
      },
      { arrayFormat: 'comma' }
    )}`,
    options
  )

export const useFetchSupplierEvents = <T>(
  supplierId: string,
  orderByProperty?: string,
  options?: UseFetchOptions<T>
) => {
  const isSubdomain = useIsSubdomain()
  const paramsObj = {
    limit: '50',
    include: ['categories', 'address.locality', 'supplier'],
    ...(orderByProperty && { orderBy: orderByProperty }),
    search: `supplier_id:${supplierId};hasEvent:false;${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}`,
  }

  const url = `v1/store/events?${qs.stringify(paramsObj, {
    // encodeValuesOnly: true, // prettify URL
    encode: false,
    arrayFormat: 'comma',
  })}`
  return useKftApiFetch(url, options)
}

export const useFetchSimilarEvents = <T>(
  eventPermalink: string,
  partnerId: string,
  options?: UseFetchOptions<T>
) => {
  const paramsObj = {
    orderBy: 'distance',
    sortedBy: 'asc',
    search: `supplier_id:${partnerId};hasEvent:true`,
    include: ['supplier', 'categories'],
    searchFields: 'supplier_id:!='
  }

  const url = `v1/store/events/${eventPermalink}/similar?${qs.stringify(
    paramsObj,
    {
      encode: false,
      arrayFormat: 'comma',
    }
  )}`
  return useKftApiFetch(url, options)
}

export const useFetchDateRequestSimilarEvents = <
  T = DateRequestSimilarEventsResponse,
>(
  dateRequestId: string,
  options?: UseFetchOptions<T>
) => {
  const url = `v1/store/date-requests/${dateRequestId}/similar-events?${qs.stringify(
    {
      include: ['supplier', 'categories'],
    },
    {
      encode: false,
      arrayFormat: 'comma',
    }
  )}`
  return useKftApiFetch(url, options)
}

export const useFetchSimilarOnlineEvents = <T>(
  category: string,
  options?: UseFetchOptions<T>
) => {
  const paramsObj = {
    limit: '8',
    include: ['supplier', 'categories'],
    // search: { hasEvent: true, type: 'ONLINE', category },
    search: `hasEvent:true;type:ONLINE;category:${category}`,
  }

  const url = `v1/store/events?${qs.stringify(paramsObj, {
    encode: false,
    arrayFormat: 'comma',
  })}`
  return useKftApiFetch(url, options)
}

export const useFetchSimilarKits = <T>(
  category: string,
  limit?: number,
  options?: UseFetchOptions<T>
) => {
  const paramsObj = {
    limit: limit || 8,
    include: ['supplier', 'categories'],
    search: `specialization:KIT;hasEvent:false${category ? `;category:${category}` : ''}`,
    orderBy: ['has_events', 'has_promotion', 'trending_90_days'],
    sortedBy: 'desc',
  }

  const url = `v1/store/events?${qs.stringify(paramsObj, {
    encode: false,
    arrayFormat: 'comma',
  })}`
  return useKftApiFetch(url, options)
}
